import type { DocumentRecord, RequestUploadInput } from '@bcl/shared';
import { api } from '@/lib/api';

export type UploadUrlResponse = {
  document: DocumentRecord;
  uploadUrl: string;
};

export async function requestUploadUrl(
  input: RequestUploadInput,
): Promise<UploadUrlResponse> {
  const { data } = await api.post<UploadUrlResponse>('/documents/upload-url', input);
  return data;
}

/**
 * Upload direct navigateur → S3 (l'octet ne transite jamais par notre serveur).
 * Utilise XHR pour exposer une vraie progression (`onProgress`, 0→100). Garde le
 * retry sans Content-Type quand S3 renvoie un 4xx au premier essai.
 */
export async function uploadToPresignedUrl(
  url: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const attempt = (withContentType: boolean) =>
    new Promise<number>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      if (withContentType) {
        xhr.setRequestHeader(
          'Content-Type',
          file.type || 'application/octet-stream',
        );
      }
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => resolve(xhr.status);
      xhr.onerror = () => reject(new Error('Upload échoué (réseau)'));
      xhr.send(file);
    });

  // Pratique S3 recommandée : Content-Type au premier essai.
  let status = await attempt(true);
  // 4xx (typiquement « signature does not match ») → on réessaie sans
  // Content-Type explicite (la signature pré-signée ne le contient plus).
  if (status >= 400 && status < 500) {
    status = await attempt(false);
  }
  if (status < 200 || status >= 300) {
    throw new Error(`Upload échoué (${status})`);
  }
  onProgress?.(100);
}

export async function listDocumentsForProject(
  projectId: string,
): Promise<DocumentRecord[]> {
  const { data } = await api.get<DocumentRecord[]>(
    `/documents/by-project/${projectId}`,
  );
  return data;
}

export type MineDocument = DocumentRecord & { projectReference: string };

export async function listMineDocuments(): Promise<MineDocument[]> {
  const { data } = await api.get<MineDocument[]>('/documents/mine');
  return data;
}

/**
 * Chaîne complète d'upload : demande l'URL pré-signée puis PUT le fichier.
 * Renvoie le DocumentRecord créé.
 */
export async function uploadDocument(input: {
  projectId: string;
  documentType: RequestUploadInput['documentType'];
  file: File;
}): Promise<DocumentRecord> {
  const { uploadUrl, document } = await requestUploadUrl({
    projectId: input.projectId,
    documentType: input.documentType,
    filename: input.file.name,
    contentType: input.file.type || 'application/octet-stream',
    sizeBytes: input.file.size,
  });
  await uploadToPresignedUrl(uploadUrl, input.file);
  return document;
}

export async function getDownloadUrl(documentId: string): Promise<string> {
  const { data } = await api.get<{ url: string }>(
    `/documents/${documentId}/download-url`,
  );
  return data.url;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/documents/${documentId}`);
}
